import { BLUESKY_CARDYB_URL } from '@/constants/api'
import type { LinkCardMetadata } from '@/types/linkCard'

interface CardybResponse {
    url?: string
    title?: string
    description?: string
    image?: string
    error?: string
}

function hostnameCard(url: string): LinkCardMetadata {
    try {
        return { url, title: new URL(url).hostname, description: '', image: '' }
    } catch {
        throw new Error('Failed to fetch link card: invalid URL')
    }
}

export async function fetchLinkCardMetadata(
    url: string,
    signal?: AbortSignal
): Promise<LinkCardMetadata> {
    const res = await fetch(`${BLUESKY_CARDYB_URL}/v1/extract?url=${encodeURIComponent(url)}`, {
        signal,
    })

    if (!res.ok) {
        // 5xx — cardyb could not process the URL at all; fall back to hostname card
        if (res.status >= 500) {
            return hostnameCard(url)
        }

        // 400 with the standard cardyb error shape (OG-less site) — fall back to hostname card
        if (res.status === 400) {
            try {
                const body: CardybResponse = await res.json()
                if (body.error && !body.title && !body.description && !body.image) {
                    return hostnameCard(url)
                }
            } catch {
                // fall through to throw below
            }
        }

        throw new Error(`Failed to fetch link card: ${res.status}`)
    }

    const data: CardybResponse = await res.json()

    return {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty url in the response is invalid, fall back to the original request url
        url: data.url || url,
        title: data.title ?? '',
        description: data.description ?? '',
        image: data.image ?? '',
    }
}
